/**
 * Pure view logic for the TEST-CLINIC JOURNEY stepper
 * (ADORACARE-VISIT-CASE-SPINE-V1). No fetch, no React -- fully testable.
 *
 * THE JOURNEY: lead -> patient -> visit/case -> treatment -> collected
 * payment, as ONE clear five-step flow over EXISTING writers. Every step
 * state derives from REAL wire fields (never fabricated): the journey
 * context (api/routes/journey.ts) plus the caller's role.
 *
 * SEMANTICS (the reciprocal boundary, mirrored from the backend): an
 * APPOINTMENT is the plan; a VISIT (case sheet) is what happened. Step 3's
 * check-in actions compose the EXISTING S2-P3 endpoints; nothing in this
 * module represents a journey-owned appointment-status or lead-stage write.
 */

import type { DashboardMe } from '@/dashboard/types';
import type {
  JourneyCaseSheetView,
  JourneyLeadContext,
} from '@/dashboard/api';

// --- access (mirrors decideLeadCockpitAccess) --------------------------------

/**
 * The LEGACY (gating OFF) staff roles the test-journey PAGE is offered to.
 * Narrowed by RECEPTIONIST-EXPERIENCE-STITCH-V1 (F13; 04-SURFACE-FATES row
 * 27) to the demo drivers OWNER + GM_AGM: the test-lab stepper is off every
 * operator's primary nav, and the route fail-closes (redirect to homePath)
 * for a narrowed-out role. Backend mirror:
 * TEST_JOURNEY_LEGACY_PAGE_ROLES (lib/domain/journey/test-journey-gating.ts).
 *
 * TEST-SPINE-GATING-V1: under GATING ON (the seeded default for every org)
 * this set no longer decides -- the server-computed /me door signals do
 * (see decideTestJourneyAccess below), and both /api/journey routes enforce
 * the SAME decision server-side. This set remains ONLY the gating-OFF
 * (rollback) audience, restoring the prior behavior exactly.
 */
export const TEST_JOURNEY_ROLES: ReadonlySet<DashboardMe['role']> = new Set<
  DashboardMe['role']
>(['gm_agm', 'owner']);

export type TestJourneyAccessDecision =
  | { kind: 'wait' }
  | { kind: 'redirect'; to: string }
  /** TEST-SPINE-GATING-V1: gated org, refused caller -- render the honest
   *  403 card (the DashboardLoadError access-restricted primitive), never a
   *  blank and never a silent home redirect. */
  | { kind: 'deny' }
  | { kind: 'render' };

/**
 * The route decision, in precedence order (TEST-SPINE-GATING-V1):
 *   1. me.testJourneyEnabled -- the server says this door is lawfully
 *      openable (gated-and-allowed, or gating-off legacy audience): render.
 *   2. me.testJourneyGated -- the org runs the gate and the server did NOT
 *      light the door: honest 403 card (deny).
 *   3. Neither signal (gating OFF, or a /me fragment read fault): the
 *      LEGACY decision exactly as shipped by ADORACARE-VISIT-CASE-SPINE-V1 /
 *      F13 -- demo drivers render, every other role redirects to its own
 *      homePath. On a fragment fault under gating ON this renders the shell
 *      for a legacy role, whose context read then refuses 403 server-side --
 *      the backend gate remains the boundary (fail-closed where it counts).
 */
export function decideTestJourneyAccess(state: {
  loading: boolean;
  error: string | null;
  me: DashboardMe | null;
}): TestJourneyAccessDecision {
  if (state.loading) return { kind: 'wait' };
  if (state.error || !state.me) {
    return { kind: 'redirect', to: '/login' };
  }
  if (state.me.testJourneyEnabled === true) {
    return { kind: 'render' };
  }
  if (state.me.testJourneyGated === true) {
    return { kind: 'deny' };
  }
  if (!TEST_JOURNEY_ROLES.has(state.me.role)) {
    return { kind: 'redirect', to: state.me.homePath };
  }
  return { kind: 'render' };
}

// --- per-role step capabilities (UI hints; the backend gate is the truth) ----

/** Link patient = the PATIENT_MASTER_CREATE_ROLES audience. */
export function canLinkPatient(role: DashboardMe['role']): boolean {
  return (
    role === 'receptionist' || role === 'coc' || role === 'gm_agm' || role === 'owner'
  );
}

/** Book / check-in = the APPOINTMENT_ACTION_ROLES audience. */
export function canOperateAppointment(role: DashboardMe['role']): boolean {
  return (
    role === 'receptionist' || role === 'coc' || role === 'gm_agm' || role === 'owner'
  );
}

/** Case-sheet author = doctor only (the compliance wall's write side). */
export function canWriteCaseSheet(role: DashboardMe['role']): boolean {
  return role === 'doctor';
}

/** Payment entry surface audience (config-resolved server-side; mirror of
 *  BILLING_ENTRY_ROLES -- receptionist seeded + coc/owner supervise). */
export function canRecordPayment(role: DashboardMe['role']): boolean {
  return role === 'receptionist' || role === 'coc' || role === 'owner';
}

// --- the five steps -----------------------------------------------------------

export type JourneyStepKey =
  | 'lead'
  | 'patient'
  | 'visit'
  | 'treatment'
  | 'payment';

export type JourneyStepStatus = 'done' | 'current' | 'upcoming';

export interface JourneyStepState {
  readonly key: JourneyStepKey;
  readonly index: number;
  readonly title: string;
  readonly status: JourneyStepStatus;
  /** One honest line describing where this step stands (real fields only). */
  readonly summary: string;
}

export const JOURNEY_STEP_TITLES: Record<JourneyStepKey, string> = {
  lead: 'Lead',
  patient: 'Patient',
  visit: 'Visit',
  treatment: 'Diagnosis & treatment',
  payment: 'Payment',
};

/** Appointment statuses that mean "the visit happened / is happening". */
const VISIT_ARRIVED_STATUSES: ReadonlySet<string> = new Set([
  'arrived',
  'checked_in',
  'fulfilled',
  'completed',
]);

export function isAppointmentFulfilled(status: string | null): boolean {
  return status === 'fulfilled' || status === 'completed';
}

/** The context's current appointment row (by currentAppointmentId), if any. */
export function currentAppointment(
  ctx: JourneyLeadContext,
): JourneyLeadContext['appointments'][number] | null {
  if (ctx.currentAppointmentId === null) return null;
  return (
    ctx.appointments.find((a) => a.appointmentId === ctx.currentAppointmentId) ??
    null
  );
}

/** TRUE when the case sheet exists and is finalized (any audience shape). */
export function isCaseSheetFinalized(view: JourneyCaseSheetView | null): boolean {
  return view !== null && view.status === 'finalized';
}

/** TRUE when at least one plan item exists (audience-aware; the analytics
 *  view carries itemCount instead of rows). */
export function caseSheetHasPlanItems(view: JourneyCaseSheetView | null): boolean {
  if (view === null) return false;
  if (view.audience === 'analytics') return view.itemCount > 0;
  return view.planItems.length > 0;
}

/**
 * Derive the five step states from the loaded context. Completion is judged
 * on REAL fields only:
 *   lead      done when the lead is loaded AND has a clinic (org-plane
 *             pending leads route to the EXISTING org-intake set-clinic flow)
 *   patient   done when leads.patient_id is stamped (the identity link)
 *   visit     done when the current appointment reached arrived or beyond
 *   treatment done when the visit case sheet is FINALIZED
 *   payment   done when at least one non-voided payment exists
 * The CURRENT step is the first not-done step; everything after is upcoming.
 */
export function deriveJourneySteps(
  ctx: JourneyLeadContext | null,
): readonly JourneyStepState[] {
  const appt = ctx === null ? null : currentAppointment(ctx);
  const done: Record<JourneyStepKey, boolean> = {
    lead: ctx !== null && ctx.lead.clinicId !== null,
    patient: ctx !== null && ctx.conversion.patientId !== null,
    visit: appt !== null && VISIT_ARRIVED_STATUSES.has(appt.status),
    treatment: ctx !== null && isCaseSheetFinalized(ctx.caseSheet),
    payment: ctx !== null && ctx.payments !== null && ctx.payments.count > 0,
  };

  const summaries: Record<JourneyStepKey, string> = {
    lead:
      ctx === null
        ? 'Select a lead to begin.'
        : ctx.lead.clinicId === null
          ? 'Lead is pending a clinic - assign one in Org intake first.'
          : `${ctx.lead.name ?? 'Unnamed lead'} at ${ctx.lead.clinicLabel ?? `clinic #${ctx.lead.clinicId}`}.`,
    patient:
      ctx === null
        ? 'Link the lead to a patient record.'
        : ctx.conversion.patientId !== null
          ? ctx.patient !== null
            ? `Linked to ${ctx.patient.fullName ?? 'patient'}${ctx.patient.umr ? ` (${ctx.patient.umr})` : ''}.`
            : 'Linked to a patient record.'
          : 'Not linked to a patient yet.',
    visit:
      appt === null
        ? 'No appointment yet - book one to plan the visit.'
        : `Appointment #${appt.appointmentId} is ${appointmentStatusLabel(appt.status)}.`,
    treatment:
      ctx === null || ctx.caseSheet === null
        ? 'No visit record yet - the doctor starts it once the patient arrives.'
        : isCaseSheetFinalized(ctx.caseSheet)
          ? 'Visit record finalized.'
          : 'Visit record in draft.',
    payment:
      ctx === null || ctx.payments === null
        ? 'Payment recording opens after the patient is linked.'
        : ctx.payments.count > 0
          ? `${ctx.payments.count} payment${ctx.payments.count === 1 ? '' : 's'} collected.`
          : 'No payment collected yet.',
  };

  const order: readonly JourneyStepKey[] = [
    'lead',
    'patient',
    'visit',
    'treatment',
    'payment',
  ];
  const firstNotDone = order.findIndex((k) => !done[k]);
  return order.map((key, i) => ({
    key,
    index: i + 1,
    title: JOURNEY_STEP_TITLES[key],
    status:
      done[key] && (firstNotDone === -1 || i < firstNotDone)
        ? 'done'
        : i === firstNotDone
          ? 'current'
          : done[key]
            ? 'done'
            : 'upcoming',
    summary: summaries[key],
  }));
}

/** TRUE when every step is done (the completion banner). */
export function isJourneyComplete(ctx: JourneyLeadContext | null): boolean {
  return deriveJourneySteps(ctx).every((s) => s.status === 'done');
}

// --- labels --------------------------------------------------------------------

/** Honest human label for a stored appointment status token. */
export function appointmentStatusLabel(status: string): string {
  switch (status) {
    case 'booked':
      return 'booked';
    case 'confirmed':
      return 'confirmed';
    case 'rescheduled':
      return 'rescheduled';
    case 'cancelled':
      return 'cancelled';
    case 'no_show':
      return 'a no-show';
    case 'arrived':
      return 'arrived';
    case 'checked_in':
      return 'checked in';
    case 'fulfilled':
      return 'fulfilled';
    case 'completed':
      return 'completed';
    case 'waitlist':
      return 'waitlisted';
    default:
      return status;
  }
}

/** The check-in chain position for the visit lane chips. */
export const VISIT_CHAIN: readonly {
  readonly status: string;
  readonly label: string;
}[] = [
  { status: 'booked', label: 'Booked' },
  { status: 'arrived', label: 'Arrived' },
  { status: 'checked_in', label: 'Checked in' },
  { status: 'fulfilled', label: 'Fulfilled' },
];

const VISIT_CHAIN_RANK: Record<string, number> = {
  booked: 0,
  confirmed: 0,
  rescheduled: 0,
  arrived: 1,
  checked_in: 2,
  fulfilled: 3,
  completed: 3,
};

/** Rank of a status on the check-in chain; -1 when off-chain (cancelled etc). */
export function visitChainRank(status: string): number {
  const r = VISIT_CHAIN_RANK[status];
  return r === undefined ? -1 : r;
}

/** The next check-in action for an on-chain appointment, or null. The tokens
 *  name the EXISTING S2-P3 endpoints -- the journey only composes them. */
export function nextVisitAction(
  status: string,
): 'arrived' | 'checked_in' | 'fulfilled' | null {
  switch (visitChainRank(status)) {
    case 0:
      return 'arrived';
    case 1:
      return 'checked_in';
    case 2:
      return 'fulfilled';
    default:
      return null;
  }
}
